import React, { useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useDispatch } from "react-redux";

import Footer from "./components/Footer";
import ErrorBoundary from "./components/ErrorBoundary";
import NavbarEntry from "./components/navbar/NavbarEntry";
import CookieConsent from "./components/cookie/CookieConsent";
import CustomerChatWidget from "./components/chat/CustomerChatWidget";

import PrivateRoute from "./routes/PrivateRoute";
import AdminRoute from "./routes/AdminRoute";

import { checkUserSession } from "./actions/userActions";
import { getCart } from "./actions/cartActions";

import HomeScreen from "./screens/home/HomeScreen";
import SearchScreen from "./screens/search/SearchScreen";
import WishlistScreen from "./screens/wishlist/WishlistScreen";

import CartScreenEntry from "./screens/entries/CartScreenEntry";
import ProductScreenEntry from "./screens/entries/ProductScreenEntry";
import ShopEntry from "./screens/entries/ShopEntry";
import ShopLandingScreen from "./screens/home/ShopLandingScreen";

import ProductionScreen from "./screens/portfolio/ProductionScreen";
import BespokeScreen from "./screens/portfolio/BespokeScreen";
import AdminMadeToMeasureScreen from "./screens/madeToMeasure/AdminMadeToMeasureScreen";
import MadeToMeasureEnquiryStatusScreen from "./screens/madeToMeasure/MadeToMeasureEnquiryStatusScreen";
import MadeToMeasurePaymentScreen from "./screens/madeToMeasure/MadeToMeasurePaymentScreen";
import MadeToMeasureScreen from "./screens/madeToMeasure/MadeToMeasureScreen";
import MadeToMeasureQuoteScreen from "./screens/madeToMeasure/MadeToMeasureQuoteScreen";

import CreditsScreen from "./screens/credits/CreditsScreen";
import ContactScreen from "./screens/contacts/ContactScreen";
import AboutScreen from "./screens/about/AboutScreen";
import LegalPageScreen from "./screens/legal/LegalPageScreen";
import ServiceOrderTrackerScreen from "./screens/services/ServiceOrderTrackerScreen";
import ServicesScreen from "./screens/services/ServicesScreen";
import JewelryHireScreen from "./screens/jewelryHire/JewelryHireScreen";
import NewsletterUnsubscribeScreen from "./screens/newsletter/NewsletterUnsubscribeScreen";

import LoginScreen from "./screens/auth/LoginScreen";
import RegisterScreen from "./screens/auth/RegisterScreen";
import ForgotPasswordScreen from "./screens/auth/ForgotPasswordScreen";
import ResetPasswordScreen from "./screens/auth/ResetPasswordScreen";
import ProfileScreen from "./screens/auth/ProfileScreen";

import CheckoutScreen, {
  ShippingScreen,
} from "./screens/checkout/CheckoutScreen";
import CheckoutSuccessScreen, {
  CheckoutCancelScreen,
} from "./screens/checkout/CheckoutSuccessScreen";
import {
  PayPalCancelScreen,
  PayPalReturnScreen,
} from "./screens/checkout/PayPalReturnScreen";
import SumUpReturnScreen from "./screens/checkout/SumUpReturnScreen";

import OrderScreen from "./screens/orders/OrderScreen";
import OrderHistoryScreen from "./screens/orders/OrderHistoryScreen";
import ReceiptScreen from "./screens/orders/ReceiptScreen";

import AdminDashboardScreen from "./screens/admin/AdminDashboardScreen";
import AdminProductsListScreen from "./screens/admin/AdminProductsListScreen";
import AdminProductFormScreen from "./screens/admin/AdminProductFormScreen";

import AdminPortfolioScreen from "./screens/admin/AdminPortfolioScreen";
import AdminPortfolioFormScreen from "./screens/admin/AdminPortfolioFormScreen";
import AdminJewelryHireItemsScreen from "./screens/admin/AdminJewelryHireItemsScreen";
import AdminJewelryHireItemFormScreen from "./screens/admin/AdminJewelryHireItemFormScreen";

import AdminCreditsScreen from "./screens/admin/AdminCreditsScreen";
import AdminCreditsFormScreen from "./screens/admin/AdminCreditsFormScreen";

import AdminContactListScreen from "./screens/admin/AdminContactListScreen";
import AdminContactFormScreen from "./screens/admin/AdminContactFormScreen";
import AdminAboutScreen from "./screens/admin/AdminAboutScreen";
import AdminLegalEditScreen from "./screens/admin/AdminLegalEditScreen";
import AdminLegalListScreen from "./screens/admin/AdminLegalListScreen";
import AdminMessagesScreen from "./screens/admin/AdminMessagesScreen";
import AdminPromoCodeFormScreen from "./screens/admin/AdminPromoCodeFormScreen";
import AdminPromoCodesScreen from "./screens/admin/AdminPromoCodesScreen";
import AdminServiceBookingsScreen from "./screens/admin/AdminServiceBookingsScreen";
import AdminServiceFormScreen from "./screens/admin/AdminServiceFormScreen";
import AdminServiceOrdersScreen from "./screens/admin/AdminServiceOrdersScreen";
import AdminServicesScreen from "./screens/admin/AdminServicesScreen";
import AdminUsersScreen from "./screens/admin/AdminUsersScreen";
import AdminNewsletterSubscribersScreen from "./screens/admin/AdminNewsletterSubscribersScreen";

import AdminCategoryListScreen from "./screens/admin/AdminCategoryListScreen";
import AdminCategoryFormScreen from "./screens/admin/AdminCategoryFormScreen";

import AdminSubcategoryListScreen from "./screens/admin/AdminSubcategoryListScreen";
import AdminSubcategoryFormScreen from "./screens/admin/AdminSubcategoryFormScreen";

import AdminOrdersScreen from "./screens/orders/admin/AdminOrdersScreen";
import AdminOrderManagementScreen from "./screens/orders/admin/AdminOrderManagementScreen";
import AdminOrderPrintScreen from "./screens/orders/admin/AdminOrderPrintScreen";
import AdminPickingSlipScreen from "./screens/orders/admin/AdminPickingSlipScreen";
import AdminOrderAnalyticsScreen from "./screens/orders/admin/AdminOrderAnalyticsScreen";
import AdminReturnsScreen from "./screens/orders/admin/AdminReturnsScreen";

import NotFoundScreen from "./screens/NotFoundScreen";
import AccessDeniedScreen from "./screens/AccessDeniedScreen";

function RouteBoundary({ boundaryName, children }) {
  const location = useLocation();

  return (
    <ErrorBoundary boundaryName={boundaryName} resetKey={location.pathname}>
      {children}
    </ErrorBoundary>
  );
}

export default function App() {
  const dispatch = useDispatch();
  const didBootstrapRef = useRef(false);

  useEffect(() => {
    if (didBootstrapRef.current) return;

    didBootstrapRef.current = true;

    dispatch(checkUserSession());
    dispatch(getCart());
  }, [dispatch]);

  const isolate = (element, boundaryName) => (
    <RouteBoundary boundaryName={boundaryName}>{element}</RouteBoundary>
  );

  return (
    <Router>
      <ErrorBoundary boundaryName="Navigation">
        <NavbarEntry />
      </ErrorBoundary>

      <ToastContainer position="top-right" newestOnTop />

      <div style={{ minHeight: "80vh" }}>
        <ErrorBoundary boundaryName="Page">
          <Routes>
            <Route path="/" element={isolate(<HomeScreen />, "Home")} />
            <Route
              path="/search"
              element={isolate(<SearchScreen />, "Search")}
            />
            <Route
              path="/wishlist"
              element={isolate(<WishlistScreen />, "Wishlist")}
            />

            <Route
              path="/shop"
              element={isolate(<ShopLandingScreen />, "Shop")}
            />
            <Route
              path="/shop/:shopKey"
              element={isolate(<ShopEntry />, "Shop")}
            />
            <Route
              path="/shop/:shopKey/product/:identifier"
              element={isolate(<ProductScreenEntry />, "Product")}
            />

            <Route path="/login" element={isolate(<LoginScreen />, "Login")} />
            <Route
              path="/register"
              element={isolate(<RegisterScreen />, "Register")}
            />
            <Route
              path="/verify-email"
              element={isolate(<RegisterScreen />, "Verify email")}
            />
            <Route
              path="/forgot-password"
              element={isolate(<ForgotPasswordScreen />, "Password reset")}
            />
            <Route
              path="/reset-password/:token"
              element={isolate(<ResetPasswordScreen />, "Password reset")}
            />

            <Route
              path="/cart"
              element={isolate(<CartScreenEntry />, "Cart")}
            />
            <Route
              path="/costume-production"
              element={<Navigate to="/production" replace />}
            />
            <Route
              path="/production"
              element={isolate(<ProductionScreen />, "Production")}
            />
            <Route
              path="/bespoke-tailoring"
              element={<Navigate to="/bespoke" replace />}
            />
            <Route
              path="/bespoke"
              element={isolate(<BespokeScreen />, "Bespoke")}
            />
            <Route
              path="/vintage-fabrics"
              element={<Navigate to="/shop/textiles" replace />}
            />
            <Route
              path="/haberdashery"
              element={<Navigate to="/shop/haberdashery" replace />}
            />
            <Route
              path="/made-to-measure"
              element={isolate(<MadeToMeasureScreen />, "Made to measure")}
            />
            <Route
              path="/made-to-measure/enquiry/:token"
              element={isolate(
                <MadeToMeasureEnquiryStatusScreen />,
                "Made to measure",
              )}
            />
            <Route
              path="/made-to-measure/quote/:token"
              element={isolate(<MadeToMeasureQuoteScreen />, "Made to measure")}
            />
            <Route
              path="/credits"
              element={isolate(<CreditsScreen />, "Credits")}
            />
            <Route
              path="/services"
              element={isolate(<ServicesScreen />, "Services")}
            />
            <Route
              path="/jewelry-hire"
              element={isolate(<JewelryHireScreen />, "Jewellery Hire")}
            />
            <Route
              path="/jewelry-hire/*"
              element={<Navigate to="/jewelry-hire" replace />}
            />
            <Route
              path="/services/button-holes"
              element={isolate(
                <ServicesScreen serviceCategory="buttonHole" />,
                "Button Hole Service",
              )}
            />
            <Route
              path="/services/cover-buttons"
              element={isolate(
                <ServicesScreen serviceCategory="coverButton" />,
                "Cover Button Service",
              )}
            />
            <Route
              path="/contact"
              element={isolate(<ContactScreen />, "Contact")}
            />
            <Route path="/about" element={isolate(<AboutScreen />, "About")} />
            <Route
              path="/newsletter/unsubscribe"
              element={isolate(
                <NewsletterUnsubscribeScreen />,
                "Newsletter preferences",
              )}
            />
            <Route
              path="/terms-and-conditions"
              element={isolate(
                <LegalPageScreen slug="terms-and-conditions" />,
                "Legal",
              )}
            />
            <Route
              path="/privacy-policy"
              element={isolate(
                <LegalPageScreen slug="privacy-policy" />,
                "Legal",
              )}
            />
            <Route
              path="/cookie-policy"
              element={isolate(
                <LegalPageScreen slug="cookie-policy" />,
                "Legal",
              )}
            />
            <Route
              path="/legal/:slug"
              element={isolate(<LegalPageScreen />, "Legal")}
            />
            <Route
              path="/terms"
              element={<Navigate to="/terms-and-conditions" replace />}
            />
            <Route
              path="/privacy"
              element={<Navigate to="/privacy-policy" replace />}
            />
            <Route
              path="/cookies"
              element={<Navigate to="/cookie-policy" replace />}
            />

            <Route element={isolate(<PrivateRoute />, "Account and checkout")}>
              <Route path="/profile" element={<ProfileScreen />} />

              <Route
                path="/services/orders/:reference"
                element={<ServiceOrderTrackerScreen />}
              />

              <Route
                path="/made-to-measure/:requestId"
                element={<MadeToMeasurePaymentScreen />}
              />
              <Route
                path="/made-to-measure/:requestId/pay"
                element={<MadeToMeasurePaymentScreen />}
              />

              <Route path="/checkout/shipping" element={<ShippingScreen />} />
              <Route path="/checkout" element={<CheckoutScreen />} />
              <Route
                path="/checkout/paypal/return"
                element={<PayPalReturnScreen />}
              />
              <Route
                path="/checkout/paypal/return/:orderNumber"
                element={<PayPalReturnScreen />}
              />
              <Route
                path="/checkout/paypal/cancel"
                element={<PayPalCancelScreen />}
              />
              <Route
                path="/checkout/paypal/cancel/:orderNumber"
                element={<PayPalCancelScreen />}
              />
              <Route
                path="/checkout/sumup/return"
                element={<SumUpReturnScreen />}
              />
              <Route
                path="/checkout/sumup/return/:orderNumber"
                element={<SumUpReturnScreen />}
              />
              <Route
                path="/payment/sumup/return"
                element={<SumUpReturnScreen />}
              />
              <Route
                path="/payment/sumup/return/:orderNumber"
                element={<SumUpReturnScreen />}
              />
              <Route
                path="/checkout/success"
                element={<CheckoutSuccessScreen />}
              />
              <Route
                path="/order/:orderNumber/success"
                element={<CheckoutSuccessScreen />}
              />
              <Route
                path="/order/:orderNumber/cancel"
                element={<CheckoutCancelScreen />}
              />

              <Route path="/orders" element={<OrderHistoryScreen />} />
              <Route path="/order/:id" element={<OrderScreen />} />
              <Route path="/order/:id/receipt" element={<ReceiptScreen />} />
            </Route>

            <Route element={isolate(<AdminRoute />, "Admin")}>
              <Route path="/admin" element={<AdminDashboardScreen />} />
              <Route path="/admin/messages" element={<AdminMessagesScreen />} />

              <Route
                path="/admin/made-to-measure"
                element={<AdminMadeToMeasureScreen />}
              />

              <Route
                path="/admin/products"
                element={<AdminProductsListScreen />}
              />
              <Route
                path="/admin/products/create"
                element={<AdminProductFormScreen />}
              />
              <Route
                path="/admin/products/:id/edit"
                element={<AdminProductFormScreen />}
              />

              <Route
                path="/admin/portfolio"
                element={<AdminPortfolioScreen />}
              />
              <Route
                path="/admin/portfolio/new"
                element={<AdminPortfolioFormScreen />}
              />
              <Route
                path="/admin/portfolio/:id"
                element={<AdminPortfolioFormScreen />}
              />

              <Route path="/admin/credits" element={<AdminCreditsScreen />} />
              <Route
                path="/admin/credits/new"
                element={<AdminCreditsFormScreen />}
              />
              <Route
                path="/admin/credits/:id"
                element={<AdminCreditsFormScreen />}
              />

              <Route
                path="/admin/contact/messages"
                element={<AdminContactListScreen />}
              />
              <Route
                path="/admin/contact/content"
                element={<AdminContactFormScreen />}
              />

              <Route path="/admin/about" element={<AdminAboutScreen />} />
              <Route path="/admin/legal" element={<AdminLegalListScreen />} />
              <Route
                path="/admin/legal/:slug/edit"
                element={<AdminLegalEditScreen />}
              />
              <Route
                path="/admin/promo-codes"
                element={<AdminPromoCodesScreen />}
              />
              <Route
                path="/admin/promo-codes/new"
                element={<AdminPromoCodeFormScreen />}
              />
              <Route
                path="/admin/promo-codes/:id"
                element={<AdminPromoCodeFormScreen />}
              />
              <Route path="/admin/services" element={<AdminServicesScreen />} />
              <Route
                path="/admin/services/orders"
                element={<AdminServiceOrdersScreen />}
              />
              <Route
                path="/admin/services/orders/:reference"
                element={<AdminServiceOrdersScreen />}
              />
              <Route
                path="/admin/services/bookings"
                element={<AdminServiceBookingsScreen />}
              />
              <Route path="/admin/users" element={<AdminUsersScreen />} />
              <Route
                path="/admin/newsletter"
                element={<AdminNewsletterSubscribersScreen />}
              />
              <Route
                path="/admin/jewelry-hire"
                element={<AdminJewelryHireItemsScreen />}
              />
              <Route
                path="/admin/jewelry-hire/new"
                element={<AdminJewelryHireItemFormScreen />}
              />
              <Route
                path="/admin/jewelry-hire/:id/edit"
                element={<AdminJewelryHireItemFormScreen />}
              />
              <Route
                path="/admin/services/new"
                element={<AdminServiceFormScreen />}
              />
              <Route
                path="/admin/services/:id/edit"
                element={<AdminServiceFormScreen />}
              />

              <Route
                path="/admin/shop/:shopKey/categories"
                element={<AdminCategoryListScreen />}
              />
              <Route
                path="/admin/shop/:shopKey/categories/create"
                element={<AdminCategoryFormScreen />}
              />
              <Route
                path="/admin/shop/:shopKey/categories/:categoryId/edit"
                element={<AdminCategoryFormScreen />}
              />

              <Route
                path="/admin/shop/:shopKey/subcategories"
                element={<AdminSubcategoryListScreen />}
              />
              <Route
                path="/admin/shop/:shopKey/subcategories/create"
                element={<AdminSubcategoryFormScreen />}
              />
              <Route
                path="/admin/shop/:shopKey/subcategories/:subcategoryId/edit"
                element={<AdminSubcategoryFormScreen />}
              />

              <Route path="/admin/orders" element={<AdminOrdersScreen />} />
              <Route
                path="/admin/orders/analytics"
                element={<AdminOrderAnalyticsScreen />}
              />
              <Route
                path="/admin/orders/returns"
                element={<AdminReturnsScreen />}
              />
              <Route
                path="/admin/orders/:orderNumber"
                element={<AdminOrderManagementScreen />}
              />
              <Route
                path="/admin/orders/:orderNumber/print"
                element={<AdminOrderPrintScreen />}
              />
              <Route
                path="/admin/orders/:orderNumber/picking-slip"
                element={<AdminPickingSlipScreen />}
              />

              <Route
                path="/admin/order-analytics"
                element={<Navigate to="/admin/orders/analytics" replace />}
              />
              <Route
                path="/admin/access-denied"
                element={isolate(<AccessDeniedScreen />, "Access Denied")}
              />
            </Route>

            <Route
              path="*"
              element={isolate(<NotFoundScreen />, "Not found")}
            />
          </Routes>
        </ErrorBoundary>
      </div>

      <ErrorBoundary boundaryName="Footer">
        <Footer />
      </ErrorBoundary>

      <ErrorBoundary boundaryName="CookieConsent">
        <CookieConsent />
      </ErrorBoundary>

      <ErrorBoundary boundaryName="CustomerChat">
        <CustomerChatWidget />
      </ErrorBoundary>
    </Router>
  );
}
